<template>
  <div
    v-loading="loadingTranscripts && displayedTranscripts.length === 0"
    element-loading-text="Loading voice notes transcripts..."
  >
    <div
      class="infinite-list-wrapper"
      v-infinite-scroll="loadMore"
      infinite-scroll-disabled="disabled"
      infinite-scroll-distance="100"
    >
      <el-row :gutter="12" class="voice-grid">
        <el-col
          v-for="vn in displayedTranscripts"
          :key="vn.id"
          :xs="24"
          :sm="12"
          :md="12"
          :lg="12"
          :xl="12"
          class="voice-col"
        >
          <el-card
            shadow="hover"
            class="voice-card"
            :body-style="{
              padding: '15px',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }"
          >
            <div slot="header">
              <el-row type="flex" justify="space-between" align="middle">
                <el-rate v-model="vn.score" disabled />
                <div class="header-badges">
                  <el-tag v-if="vn.approved" type="success" effect="dark" size="small">
                    <i class="fa fa-podcast" style="margin-right: 4px"></i> On Air
                  </el-tag>
                  <el-tag v-if="vn.flagged" type="danger" effect="plain" size="small">
                    <i class="fa fa-flag" style="margin-right: 4px"></i> Flagged
                  </el-tag>
                </div>
              </el-row>
            </div>

            <div class="voice-content">
              <div class="note-name">
                <i class="el-icon-user"></i> <strong>{{ vn.sender }}</strong>
              </div>
              <div v-if="vn.location" class="note-location">
                <i class="el-icon-location"></i> {{ vn.location }}
              </div>
              <div class="note-time">
                <i class="el-icon-time"></i> {{ displayDatetime(vn.timestamp) }}
              </div>

              <div class="note-summary">{{ vn.transcript }}</div>

              <div class="note-tags" v-if="vn.tags && vn.tags.length">
                <span class="tag-label">Tags</span>
                <el-tag
                  v-for="tag in vn.tags.slice(0, 2)"
                  :key="tag"
                  size="mini"
                  effect="light"
                  class="tag"
                >
                  <i class="fa fa-tag"></i> {{ tag }}
                </el-tag>
                <el-tag v-if="vn.tags.length > 2" size="mini" type="info" effect="plain">
                  +{{ vn.tags.length - 2 }}
                </el-tag>
              </div>

              <div class="note-tags" v-if="vn.people && vn.people.length">
                <span class="tag-label">People</span>
                <el-tag
                  v-for="person in vn.people.slice(0, 2)"
                  :key="person"
                  size="mini"
                  type="info"
                  effect="plain"
                  class="tag"
                >
                  <i class="el-icon-user"></i> {{ person }}
                </el-tag>
                <el-tag v-if="vn.people.length > 2" size="mini" type="info" effect="plain">
                  +{{ vn.people.length - 2 }}
                </el-tag>
              </div>

              <audio
                v-if="vn.audioUrl"
                :src="vn.audioUrl"
                controls
                preload="meta"
                class="voice-audio"
              ></audio>
            </div>

            <div class="voice-actions">
              <el-button
                size="small"
                :type="vn.approved ? 'success' : ''"
                :plain="vn.approved"
                @click="onApprove(vn)"
              >
                <i :class="vn.approved ? 'fa fa-podcast' : 'fa fa-check'"></i>
                {{ vn.approved ? 'On Air' : 'Approve' }}
              </el-button>

              <el-button
                size="small"
                :type="vn.flagged ? 'danger' : ''"
                :plain="vn.flagged"
                @click="onToggleFlag(vn)"
              >
                <i :class="vn.flagged ? 'fa fa-flag' : 'fa fa-flag-o'"></i>
                {{ vn.flagged ? 'Flagged' : 'Flag' }}
              </el-button>

              <el-button
                size="small"
                type="info"
                plain
                @click="onPreviewAudio(vn)"
                style="margin-left: auto"
              >
                <i class="fa fa-eye"></i> Preview
              </el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <div v-if="displayedTranscripts.length > 0" class="no-more">
        <el-divider> <i class="el-icon-finished"></i> All voice notes loaded </el-divider>
        <p class="stats">
          Displaying {{ displayedTranscripts.length }} of {{ transcripts.length }}
        </p>
      </div>

      <div v-if="!loadingTranscripts && displayedTranscripts.length === 0" class="row">
        <div class="col-lg-12">
          <empty style="font-size: 1.2em">No voice notes found</empty>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Empty from '@/components/common/Empty';

export default {
  name: 'VoiceNotesGrid',
  components: { Empty },
  props: {
    transcripts: { type: Array, default: () => [] },
    loadingTranscripts: { type: Boolean, default: false },
  },
  data() {
    return {
      displayedTranscripts: [],
      itemsPerLoad: 3,
      disabled: false,
    };
  },
  computed: {
    getTranscripts() {
      return this.transcripts;
    },
    noMore() {
      return this.displayedTranscripts.length >= this.getTranscripts.length;
    },
  },
  watch: {
    transcripts: {
      immediate: true,
      handler(newTranscripts) {
        this.disabled = false;
        this.displayedTranscripts = newTranscripts.slice(0, this.itemsPerLoad);

        if (this.displayedTranscripts.length >= newTranscripts.length) {
          this.disabled = true;
        }
      },
    },
  },
  mounted() {
    this.displayedTranscripts = this.getTranscripts.slice(0, this.itemsPerLoad);
    if (this.displayedTranscripts.length >= this.getTranscripts.length) {
      this.disabled = true;
    }
  },
  methods: {
    displayDatetime(ts) {
      return moment(ts).fromNow();
    },
    loadMore() {
      if (this.noMore || this.loadingTranscripts || this.disabled) return;

      const nextIndex = this.displayedTranscripts.length;
      const nextItems = this.getTranscripts.slice(nextIndex, nextIndex + this.itemsPerLoad);

      if (nextItems.length) {
        this.displayedTranscripts.push(...nextItems);
      }

      if (this.displayedTranscripts.length >= this.getTranscripts.length) {
        this.disabled = true;
      }
    },
    onApprove(row) {
      this.$emit('approve-on-air', row);
    },
    onToggleFlag(row) {
      this.$emit('toggle-flag', row);
    },
    onPreviewAudio(row) {
      this.$emit('preview-audio', row);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';

.infinite-list-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 600px;
  padding-right: 6px;
}

.voice-grid {
  display: flex;
  flex-wrap: wrap;
}

.voice-col {
  display: flex;
  margin-bottom: 12px;
  margin-top: 8px;
  min-height: 380px;
}

.voice-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  transition: transform 0.2s ease;
}

.voice-card:hover {
  transform: translateY(-2px);
}

.voice-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.header-badges {
  display: flex;
  gap: 6px;
}

.note-name,
.note-location,
.note-time {
  margin-bottom: 6px;
  font-size: 13px;
  color: #666;
}

.note-summary {
  background: #fafafa;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  flex-grow: 1;
  border-left: 3px solid lighten($colony-primary, 42%);
}

.note-tags {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.tag-label {
  font-size: 13px;
  margin-right: 8px;
  color: #909399;
  font-weight: 500;
  white-space: nowrap;
}

.tag {
  margin-right: 4px;
  margin-bottom: 4px;
}

.voice-audio {
  width: 100%;
  margin-bottom: 15px;
  border-radius: 4px;
}

.voice-actions {
  margin-top: auto;
  display: flex;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  align-items: center;
}

.no-more {
  text-align: center;
  padding: 20px 0;
  color: #909399;
}

.stats {
  font-size: 12px;
  color: #c0c4cc;
}

.infinite-list-wrapper::-webkit-scrollbar {
  width: 6px;
}

.infinite-list-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.infinite-list-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.infinite-list-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>


// WEBPACK FOOTER //
// VoiceNotesGrid.vue?29e87ed4